import isEqual from "lodash/isEqual";
import { useCallback } from "react";
import { useLocalStorage } from "../../../shared/hooks/useLocalStorage";
import {
  CONSENT_CHANGED_EVENT,
  CONSENT_STORAGE_KEY,
  DEFAULT_CONSENT_PREFERENCES
} from "../constants";
import {
  CONSENT_CATEGORY_ANALYTICS,
  ConsentPreferences,
  StoredConsent
} from "../types";
import { hasPrivacySignal } from "../utils/hasPrivacySignal";
import { DEBUG_ANALYTICS } from "../../../utils/analytics";

type UseCookieConsentReturn = {
  readonly hasDecision: boolean;
  readonly preferences: ConsentPreferences;
  readonly isAnalyticsAllowed: boolean;
  readonly savePreferences: (preferences: ConsentPreferences) => void;
};

export const useCookieConsent = (): UseCookieConsentReturn => {
  const [stored, setStored] = useLocalStorage<StoredConsent | undefined>(
    CONSENT_STORAGE_KEY,
    undefined
  );

  // GPC/DNT browser signals act as an implicit "deny all" decision —
  // the user has already expressed their preference at the browser level
  const privacySignalActive = hasPrivacySignal();
  const hasDecision = privacySignalActive || stored !== undefined;
  const preferences = privacySignalActive
    ? DEFAULT_CONSENT_PREFERENCES
    : (stored?.preferences ?? DEFAULT_CONSENT_PREFERENCES);
  const analyticsAllowed =
    !privacySignalActive && preferences[CONSENT_CATEGORY_ANALYTICS];

  const savePreferences = useCallback(
    (newPreferences: ConsentPreferences) => {
      const record: StoredConsent = {
        preferences: newPreferences,
        updatedAt: new Date().toISOString(),
        version: 1
      };
      setStored(record);

      if (DEBUG_ANALYTICS) {
        console.info(
          "Analytics consent saved to localstorage",
          JSON.stringify(newPreferences)
        );
      }

      if (!isEqual(preferences, newPreferences)) {
        document.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT));

        if (DEBUG_ANALYTICS) {
          console.info(
            "Analytics consent updated",
            JSON.stringify(newPreferences)
          );
        }
      }
    },
    [setStored, preferences]
  );

  return {
    hasDecision,
    isAnalyticsAllowed: analyticsAllowed,
    preferences,
    savePreferences
  };
};
